// packages/core/src/srs/SRSEngine.ts
// ─────────────────────────────────────────────────────────────────────────
// Spaced repetition engine — V2 (3-state Anki-like SM-2 variant).
//
// Scheduler version 2. States: new · learning · review · relearning · mastered.
// Learning/relearning use minute/hour steps; review uses day intervals on a
// real wall-clock `dueAt` ISO timestamp. Pure functions only — persistence
// and session orchestration are the caller's responsibility.
//
// Backward compatibility:
//   • V1 records (dueDate 'YYYY-MM-DD' + intervalDays) are silently upgraded
//     by `migrateSrsRecord` → schedulerVersion 2.
//   • Legacy names (computeNextRecord, newCardRecord, previewIntervals,
//     isDue, isDueCard, XP_REWARD) are preserved so existing call sites and
//     the web shim keep working unchanged.
// ─────────────────────────────────────────────────────────────────────────

export type SRSRating = 'again' | 'hard' | 'good' | 'easy';

export type CardState =
	| 'new'
	| 'learning'
	| 'review'
	| 'relearning'
	| 'mastered';

// ── Canonical V2 record ──────────────────────────────────────────────────
export interface CardSRSRecord {
	cardId: string;
	state: CardState;
	stepIndex: number; // position within learning/relearning steps
	repetitions: number; // consecutive successful reviews
	lapses: number; // times the user forgot after graduating
	easeFactor: number; // starts 2.5, clamped [1.3, 3.0]
	intervalDays: number; // current review interval (days), clamped [1, 365]
	dueAt: string; // ISO timestamp — real scheduling truth
	lastReviewedAt: string | null; // ISO timestamp, for analytics
	masteryLevel: number; // 0–5, derived snapshot
	reviewHistory: ReviewHistoryEntry[]; // kept light; valuable for future FSRS
	schedulerVersion: 2;
}

export interface ReviewHistoryEntry {
	at: string; // ISO timestamp of the review
	rating: SRSRating;
	intervalDays: number; // interval assigned by this review
}

// ── Tunables ───────────────────────────────────────────────────────────────
export const MIN_EASE = 1.3;
export const MAX_EASE = 3.0;
export const MIN_INTERVAL_DAYS = 1;
export const MAX_INTERVAL_DAYS = 365;

const STARTING_EASE = 2.5;

// Sub-day steps in minutes (learning + relearning short steps).
const MIN = 60 * 1000;
const DAY = 24 * 60 * MIN;

// Learning steps for NEW cards (spec §"Learning steps").
//   Again → 1 min   (stay learning, step 0)
//   Hard  → 10 min   (stay learning)
//   Good  → 1 day    (graduate to review)
//   Easy  → 4 days   (graduate to review, with bonus)
const LEARNING_AGAIN_MS = 1 * MIN;
const LEARNING_HARD_MS = 10 * MIN;
const LEARNING_GOOD_DAYS = 1;
const LEARNING_EASY_DAYS = 4;

// Relearning steps for FAILED review cards (spec §"For failed review cards").
//   Again → 10 min   (move to relearning)
//   Hard  → 1 day     (review, reduced interval)
//   Good  → normal interval × ease
//   Easy  → larger interval
const RELEARN_AGAIN_MS = 10 * MIN;
const RELEARN_HARD_DAYS = 1;

// Review interval multipliers (spec §"Review interval formula").
const HARD_INTERVAL_MULT = 1.2;
const EASY_BONUS_MULT = 1.3;

// Ease deltas per rating on graduated review cards.
const EASE_DELTA: Record<SRSRating, number> = {
	again: -0.2,
	hard: -0.15,
	good: 0,
	easy: 0.15,
};

// ── XP (spec §"Fix XP incentives") — Hard/Good reward effort & honesty ──────
export const XP_REWARD: Record<SRSRating, number> = {
	again: 1,
	hard: 3,
	good: 3,
	easy: 2,
};

// Completion bonuses (spec §"Fix XP incentives").
export const XP_BONUS = {
	finishSession: 5,
	clearAllDue: 10,
	masterLessonFlashcards: 25,
} as const;

// ── Time helpers ─────────────────────────────────────────────────────────
function nowMs(): number {
	return Date.now();
}

function isoFromMs(ms: number): string {
	return new Date(ms).toISOString();
}

function addMs(fromMs: number, ms: number): string {
	return isoFromMs(fromMs + ms);
}

function addDaysIso(fromMs: number, days: number): string {
	return isoFromMs(fromMs + days * DAY);
}

function clamp(value: number, min: number, max: number): number {
	if (Number.isNaN(value)) return min;
	return Math.min(max, Math.max(min, value));
}

// ── Mastery (spec §"Mastery logic") ──────────────────────────────────────
export function isMastered(record: CardSRSRecord): boolean {
	return (
		(record.state === 'review' || record.state === 'mastered') &&
		record.repetitions >= 4 &&
		record.intervalDays >= 21 &&
		record.lapses <= 1
	);
}

export function inferMasteryLevel(
	repetitions: number,
	intervalDays: number,
): number {
	if (repetitions >= 4 && intervalDays >= 21) return 5;
	if (repetitions >= 3 && intervalDays >= 7) return 4;
	if (repetitions >= 2) return 3;
	if (repetitions >= 1) return 2;
	return 1;
}

// ── Construction ───────────────────────────────────────────────────────────
export function newCardRecord(cardId: string): CardSRSRecord {
	return {
		cardId,
		state: 'new',
		stepIndex: 0,
		repetitions: 0,
		lapses: 0,
		easeFactor: STARTING_EASE,
		intervalDays: 0,
		dueAt: isoFromMs(nowMs()),
		lastReviewedAt: null,
		masteryLevel: 0,
		reviewHistory: [],
		schedulerVersion: 2,
	};
}

// ── Core scheduler ─────────────────────────────────────────────────────────
export function computeNextRecord(
	record: CardSRSRecord,
	rating: SRSRating,
	atMs: number = nowMs(),
): CardSRSRecord {
	const next: CardSRSRecord = {
		...record,
		reviewHistory: [...record.reviewHistory],
	};
	next.lastReviewedAt = isoFromMs(atMs);

	const inLearning = record.state === 'new' || record.state === 'learning';
	const inRelearning = record.state === 'relearning';

	if (inLearning) {
		scheduleLearning(next, rating, atMs);
	} else if (inRelearning) {
		scheduleRelearning(next, rating, atMs);
	} else {
		// review or mastered → standard graduated scheduling
		scheduleReview(next, rating, atMs);
	}

	// Ease only moves on graduated review/relearning cards, never during the
	// initial learning steps (those are fixed-step by design).
	if (!inLearning) {
		next.easeFactor = clamp(
			record.easeFactor + EASE_DELTA[rating],
			MIN_EASE,
			MAX_EASE,
		);
	}

	next.masteryLevel = inferMasteryLevel(next.repetitions, next.intervalDays);
	if (isMastered(next)) next.state = 'mastered';

	next.reviewHistory.push({
		at: isoFromMs(atMs),
		rating,
		intervalDays: next.intervalDays,
	});
	// Keep history bounded — last 50 reviews is plenty for future FSRS seeding.
	if (next.reviewHistory.length > 50) {
		next.reviewHistory = next.reviewHistory.slice(-50);
	}

	return next;
}

function scheduleLearning(
	next: CardSRSRecord,
	rating: SRSRating,
	atMs: number,
): void {
	switch (rating) {
		case 'again':
			next.state = 'learning';
			next.stepIndex = 0;
			next.repetitions = 0;
			next.intervalDays = 0;
			next.dueAt = addMs(atMs, LEARNING_AGAIN_MS);
			break;
		case 'hard':
			next.state = 'learning';
			next.intervalDays = 0;
			next.dueAt = addMs(atMs, LEARNING_HARD_MS);
			break;
		case 'good':
			next.state = 'review';
			next.stepIndex = 0;
			next.repetitions = next.repetitions + 1;
			next.intervalDays = clamp(
				LEARNING_GOOD_DAYS,
				MIN_INTERVAL_DAYS,
				MAX_INTERVAL_DAYS,
			);
			next.dueAt = addDaysIso(atMs, next.intervalDays);
			break;
		case 'easy':
			next.state = 'review';
			next.stepIndex = 0;
			next.repetitions = next.repetitions + 1;
			next.intervalDays = clamp(
				LEARNING_EASY_DAYS,
				MIN_INTERVAL_DAYS,
				MAX_INTERVAL_DAYS,
			);
			next.dueAt = addDaysIso(atMs, next.intervalDays);
			break;
	}
}

function scheduleRelearning(
	next: CardSRSRecord,
	rating: SRSRating,
	atMs: number,
): void {
	switch (rating) {
		case 'again':
			next.state = 'relearning';
			next.stepIndex = 0;
			next.dueAt = addMs(atMs, RELEARN_AGAIN_MS);
			break;
		case 'hard':
			next.state = 'review';
			next.intervalDays = clamp(
				RELEARN_HARD_DAYS,
				MIN_INTERVAL_DAYS,
				MAX_INTERVAL_DAYS,
			);
			next.repetitions = next.repetitions + 1;
			next.dueAt = addDaysIso(atMs, next.intervalDays);
			break;
		case 'good':
			next.state = 'review';
			next.repetitions = next.repetitions + 1;
			next.intervalDays = clamp(
				Math.round(Math.max(1, next.intervalDays) * next.easeFactor),
				MIN_INTERVAL_DAYS,
				MAX_INTERVAL_DAYS,
			);
			next.dueAt = addDaysIso(atMs, next.intervalDays);
			break;
		case 'easy':
			next.state = 'review';
			next.repetitions = next.repetitions + 1;
			next.intervalDays = clamp(
				Math.round(
					Math.max(1, next.intervalDays) *
						next.easeFactor *
						EASY_BONUS_MULT,
				),
				MIN_INTERVAL_DAYS,
				MAX_INTERVAL_DAYS,
			);
			next.dueAt = addDaysIso(atMs, next.intervalDays);
			break;
	}
}

function scheduleReview(
	next: CardSRSRecord,
	rating: SRSRating,
	atMs: number,
): void {
	const baseInterval = Math.max(1, next.intervalDays);

	switch (rating) {
		case 'again':
			// Failed a graduated card → relearning + lapse.
			next.state = 'relearning';
			next.stepIndex = 0;
			next.lapses = next.lapses + 1;
			next.repetitions = 0;
			next.intervalDays = clamp(
				Math.round(baseInterval * 0.5),
				MIN_INTERVAL_DAYS,
				MAX_INTERVAL_DAYS,
			);
			next.dueAt = addMs(atMs, RELEARN_AGAIN_MS);
			break;
		case 'hard':
			next.state = 'review';
			next.repetitions = next.repetitions + 1;
			next.intervalDays = clamp(
				Math.round(baseInterval * HARD_INTERVAL_MULT),
				MIN_INTERVAL_DAYS,
				MAX_INTERVAL_DAYS,
			);
			next.dueAt = addDaysIso(atMs, next.intervalDays);
			break;
		case 'good':
			next.state = 'review';
			next.repetitions = next.repetitions + 1;
			next.intervalDays = clamp(
				Math.round(baseInterval * next.easeFactor),
				MIN_INTERVAL_DAYS,
				MAX_INTERVAL_DAYS,
			);
			next.dueAt = addDaysIso(atMs, next.intervalDays);
			break;
		case 'easy':
			next.state = 'review';
			next.repetitions = next.repetitions + 1;
			next.intervalDays = clamp(
				Math.round(baseInterval * next.easeFactor * EASY_BONUS_MULT),
				MIN_INTERVAL_DAYS,
				MAX_INTERVAL_DAYS,
			);
			next.dueAt = addDaysIso(atMs, next.intervalDays);
			break;
	}
}

// ── Interval previews (for button labels) ──────────────────────────────────
export function previewIntervals(
	record: CardSRSRecord,
	atMs: number = nowMs(),
): Record<SRSRating, string> {
	const out: Record<SRSRating, string> = {
		again: '',
		hard: '',
		good: '',
		easy: '',
	};
	(['again', 'hard', 'good', 'easy'] as SRSRating[]).forEach((rating) => {
		const next = computeNextRecord(record, rating, atMs);
		out[rating] = formatDueDelta(next.dueAt, atMs);
	});
	return out;
}

// Format the gap between now and a dueAt timestamp in human terms.
export function formatDueDelta(
	dueAtIso: string,
	atMs: number = nowMs(),
): string {
	const deltaMs = new Date(dueAtIso).getTime() - atMs;
	if (deltaMs <= 0) return 'now';

	const mins = Math.round(deltaMs / MIN);
	if (mins < 60) return mins <= 1 ? '1 min' : `${mins} min`;

	const hours = Math.round(deltaMs / (60 * MIN));
	if (hours < 24) return hours === 1 ? '1 hr' : `${hours} hr`;

	const days = Math.round(deltaMs / DAY);
	if (days < 30) return days === 1 ? '1 day' : `${days} days`;
	if (days < 365) {
		const mo = Math.round(days / 30);
		return mo === 1 ? '1 mo' : `${mo} mo`;
	}
	const yr = Math.round(days / 365);
	return yr === 1 ? '1 yr' : `${yr} yr`;
}

// ── Due checks (now operate on wall-clock dueAt) ────────────────────────────
export function isDue(record: CardSRSRecord, atMs: number = nowMs()): boolean {
	return new Date(record.dueAt).getTime() <= atMs;
}

// Back-compat alias kept for old call sites.
export function isDueCard(
	record: CardSRSRecord,
	atMs: number = nowMs(),
): boolean {
	return isDue(record, atMs);
}

// ── Migration (V1 'YYYY-MM-DD' records → V2) ───────────────────────────────
interface OldV1Record {
	cardId?: string;
	repetitions?: number;
	easeFactor?: number;
	intervalDays?: number;
	dueDate?: string; // 'YYYY-MM-DD'
	totalReviews?: number;
	lastReviewed?: string | null;
	phase?: string;
}

function isV2Record(raw: unknown): raw is CardSRSRecord {
	return (
		typeof raw === 'object' &&
		raw !== null &&
		(raw as { schedulerVersion?: number }).schedulerVersion === 2
	);
}

function isOldRecord(raw: unknown): raw is OldV1Record {
	return (
		typeof raw === 'object' &&
		raw !== null &&
		'dueDate' in (raw as Record<string, unknown>)
	);
}

// Convert a 'YYYY-MM-DD' due date to a real timestamp at 9:00 AM LOCAL time.
// Midnight makes a batch of cards feel weirdly "instantly due"; 9 AM reads
// like a sane review time (spec §"Preserve due dates").
export function parseOldDueDate(dueDate: string | undefined): string {
	if (!dueDate) return isoFromMs(nowMs());
	const parts = dueDate.split('-').map((n) => parseInt(n, 10));
	if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
		return isoFromMs(nowMs());
	}
	const [y, m, d] = parts;
	// Local 9:00 AM on that calendar date.
	const local = new Date(y, m - 1, d, 9, 0, 0, 0);
	return local.toISOString();
}

export function migrateSrsRecord(cardId: string, raw: unknown): CardSRSRecord {
	if (isV2Record(raw)) {
		// Ensure newer optional fields exist (defensive against partial writes).
		return {
			...raw,
			reviewHistory: Array.isArray(raw.reviewHistory)
				? raw.reviewHistory
				: [],
			lapses: typeof raw.lapses === 'number' ? raw.lapses : 0,
		};
	}

	if (isOldRecord(raw)) {
		const repetitions = Math.max(0, raw.repetitions ?? 0);
		const easeFactor = clamp(
			raw.easeFactor ?? STARTING_EASE,
			MIN_EASE,
			MAX_EASE,
		);
		const intervalDays = clamp(
			raw.intervalDays ?? 1,
			MIN_INTERVAL_DAYS,
			MAX_INTERVAL_DAYS,
		);

		// Old overdue, never-graduated cards → treat as learning, due now.
		const todayMs = nowMs();
		const oldDueMs = new Date(parseOldDueDate(raw.dueDate)).getTime();
		const overdueAndNew = repetitions === 0 && oldDueMs <= todayMs;

		const state: CardState = overdueAndNew
			? 'learning'
			: repetitions > 0
				? 'review'
				: 'learning';

		return {
			cardId,
			state,
			stepIndex: 0,
			repetitions,
			lapses: 0,
			easeFactor,
			intervalDays,
			dueAt: overdueAndNew
				? isoFromMs(todayMs)
				: parseOldDueDate(raw.dueDate),
			lastReviewedAt: null,
			masteryLevel: inferMasteryLevel(repetitions, intervalDays),
			reviewHistory: [],
			schedulerVersion: 2,
		};
	}

	return newCardRecord(cardId);
}

// ── Lesson-level flashcard mastery ─────────────────────────────────────────
// Mastery is lesson-owned. Sessions may mix cards across lessons, but a
// lesson's flashcards are mastered only when ALL of its cards are mastered.
//
// The caller supplies the resolved list of card records for a lesson (the
// engine stays pure and never imports curriculum data).
export function isLessonFlashcardsMastered(
	lessonCardRecords: CardSRSRecord[],
): boolean {
	if (lessonCardRecords.length === 0) return false;
	return lessonCardRecords.every(isMastered);
}

